import { Box, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { BottomSheetHeader, PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import FieldsList from "features/fieldList/FieldsList";
import {
  getArableFieldsFilteredFromArray,
  getGrainFieldsFromArray,
  getSilageFieldsFromArray,
  useArableFields,
  useFields,
} from "model";
import { useCurrentFarm } from "model/useFarmQueries";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

const Content = styled("div")({
  flex: 1,
  overflowY: "auto",
  backgroundColor: "white",
  WebkitOverflowScrolling: "touch",
  paddingTop: 16,
  paddingBottom: "calc(env(safe-area-inset-bottom))",
});

export default function FieldsListScreen() {
  const { fieldType } = useParams<{ fieldType: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: currentFarm } = useCurrentFarm();

  const handleBackToFarm = () => {
    if (currentFarm?.guid) {
      navigate(`/farm/${currentFarm.guid}`);
    }
  };

  // Use React Query for field data
  const { data: allGrassFields = [] } = useFields();
  const { data: allArableFields = [] } = useArableFields();

  // Compute field lists using helper functions
  const grainFields = useMemo(
    () => getGrainFieldsFromArray(allArableFields),
    [allArableFields]
  );
  const arableFields = useMemo(
    () => getArableFieldsFilteredFromArray(allArableFields),
    [allArableFields]
  );
  const silageFields = useMemo(
    () => getSilageFieldsFromArray(allGrassFields),
    [allGrassFields]
  );

  // Get fields based on type
  const getFields = () => {
    const type = fieldType?.toLowerCase();

    // Use exact matches for field types
    switch (type) {
      case "grain":
        return grainFields;
      case "arable":
        return arableFields;
      case "silage":
        return silageFields;
      default:
        return [];
    }
  };

  const fields = getFields();

  const getFieldIds = () => {
    const ids = fields.map((field) => field.guid);
    return ids;
  };

  // Get the translation key based on the field type
  const getTranslationKey = () => {
    // Use exact matches for translation keys
    switch (fieldType) {
      case "grain":
      case "arable":
      case "silage":
      case "pasture":
        return fieldType;
      default:
        console.warn("Unknown translation key for type:", fieldType);
        return fieldType;
    }
  };

  const title = t(`fieldTypeCards.${getTranslationKey()}.title`);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {isMobile ? (
        <BottomSheetHeader title={title} onBack={handleBackToFarm} />
      ) : (
        <PageTitleBar title={title} onBack={handleBackToFarm} />
      )}
      <Content>
        <FieldsList
          fieldIds={getFieldIds()}
          emptyText={t("fieldTypeCards.noFields")}
        />
      </Content>
    </Box>
  );
}
